import {
  CellClickedEvent,
  CellPosition,
  CellRange,
  Column,
  ColumnGroup,
  GridApi,
  IRowNode,
  SuppressKeyboardEventParams,
} from 'ag-grid-community';
import { get } from 'lodash-es';
import { Maybe, RequiredNonNullable, getIsNotNullable, getIsNullable } from 'src/app/core/nullable-utils';
import { NumberFormat } from '../../../models/datasheet';
import { extractNumberFormatFromExcelHtml } from '../../../utils/datasheet';
import { getLocaleNumberFormat } from '../../../utils/locale';
import {
  CellRendererParams,
  InferTContext,
  InferTValue,
  IxColDef,
  KeyboardEvents,
  ReadOnlyData,
} from '../interfaces/interface';

export const discoverNumberFormatFromExcelPaste = (data: string): UNDEFINED<NumberFormat> => {
  const localNumberFormat = getLocaleNumberFormat();
  const numberFormat = extractNumberFormatFromExcelHtml(data);

  if (numberFormat) {
    return numberFormat;
  } else if (!localNumberFormat) {
    return getLocaleNumberFormat();
  }
};

export const fullEditStop = (api: GridApi) => {
  api.stopEditing(false);
  api.clearFocusedCell();
  api.clearCellSelection();
};

/**
 * @deprecated This function is a workaround for improper use of grid cell data. Navigate `colDef.params.data` or `params.data` instead.
 * Function helper retrieving specific cell data.
 * @param paramData - Object
 * @param fieldId - string | number
 * @return Object
 */
export const getCellData = (paramData: Maybe<object>, fieldId?: string | number): { [key: string]: any } => {
  if (!paramData || !fieldId) {
    return {};
  }

  return paramData[`${fieldId}:data`] || {};
};

/**
 * Function helper that implememt delete key event on IX table.
 * @param params - SuppressKeyboardEventParams
 * @param actions - KeyboardEvents
 * @return boolean
 */
export const deleteKeyboardEvent = (params: SuppressKeyboardEventParams, actions: KeyboardEvents = {}) => {
  const { api, editing, event } = params;

  if (editing) {
    return false;
  }

  const isBackspaceKey = event.key === 'Backspace';
  const isDeleteKey = event.key === 'Delete';
  const { onDeletePressed, onBackspacePressed } = actions;

  if (isDeleteKey) {
    return onDeletePressed ? onDeletePressed(api) : clearSelectedCells(api, 'DeleteKeyboardEvent');
  }

  if (isBackspaceKey) {
    return onBackspacePressed ? onBackspacePressed(api) : clearSelectedCells(api, 'DeleteKeyboardEvent');
  }

  return false;
};

/**
 * Function helper that clears all selected cell that are editable.
 * @param api - ObGridApiject
 * @param [eventSource] - string
 * @return boolean
 */
const clearSelectedCells = (api: GridApi, eventSource?: string) => {
  api.getCellRanges()?.forEach(range => {
    const columns = range.columns;
    const selectedNodes = getSelectedRowNode(api, range);

    selectedNodes.forEach(selectedNode => {
      columns.forEach(column => {
        const colId = column.getColId();
        const readOnlyRow = selectedNode.data.readOnly;
        const readOnlyCell = !!getCellData(selectedNode.data, colId).readOnly;
        const readOnlyCol = !!column.getColDef()?.cellRendererParams.readOnlyCol;
        const isReadOnly = readOnlyRow || readOnlyCell || readOnlyCol;

        if (!isReadOnly) {
          const currentValue = api.getCellValue({ colKey: colId, rowNode: selectedNode });
          const newValue = currentValue?.toString() === 'true' ? false : '';
          selectedNode.setDataValue(colId, newValue, eventSource);
        }
      });
    });
  });

  return true;
};

/**
 * Function helper to get node data from cell range selection.
 * @param api - GridApi
 * @param range - CellRange
 * @return IRowNode[]
 */
export const getSelectedRowNode = (api: GridApi, range: CellRange) => {
  const nodes = getAllNodes(api);
  const startRowIndex = Math.min(Number(range.startRow?.rowIndex), Number(range.endRow?.rowIndex));
  const endRowIndex = Math.max(Number(range.startRow?.rowIndex), Number(range.endRow?.rowIndex));
  return nodes
    .filter(onlyNodesWithRowIndex)
    .filter(({ rowIndex }) => startRowIndex <= rowIndex && endRowIndex >= rowIndex);
};

type RowNodeWithRowIndex<T> = IRowNode<T> & RequiredNonNullable<Pick<IRowNode<T>, 'rowIndex'>>;
function onlyNodesWithRowIndex<T>(node: IRowNode<T>): node is RowNodeWithRowIndex<T> {
  return getIsNotNullable(node.rowIndex);
}

/**
 * Function helper to determine if rendered cell component is read only.
 * @param params - CellRendererParams
 * @return boolean
 */
export const isRenderedCellReadOnly = <
  T extends CellRendererParams<ReadOnlyData & { [key: string]: any }, InferTValue<T>, InferTContext<T>>,
>(
  params: T,
): boolean => {
  const field = params.colDef?.field;
  const readOnlyColDef = !!(params.colDef as any)?.readonly; // Should use the 'editable' flag, but that's currently not set correctly and will cause some buttons not to work
  const deliverablesGridCellReadOnly = !!getCellData(params.data, field).readOnly; // Only used by DeliverablesGridColumnBuilder
  const deliverablesGridReadOnlyCol = !!params.readOnlyCol; // Only used by DeliverablesGridColumnBuilder
  const indexTableCellReadOnly = get(params.data, `${field}readOnly`); // Only used by Ix Index Table
  const indexTableCell =
    indexTableCellReadOnly === undefined ? !!params.data?.readOnly : indexTableCellReadOnly;

  return readOnlyColDef || deliverablesGridCellReadOnly || deliverablesGridReadOnlyCol || indexTableCell;
};

/**
 * Function helper to select single row on click.
 * @param params - CellClickedEvent
 * @return void
 */
export const selectRowOnClick = (params: CellClickedEvent): void => {
  params.api.clearCellSelection();

  const columns = params.api.getColumns()?.map(column => column.getId());
  params.api.addCellRange({
    rowStartIndex: params.node.rowIndex,
    rowEndIndex: params.node.rowIndex,
    columns,
  });
};

/**
 * Function helper to get all table nodes.
 * @param api - GridApi
 * @return IRowNode<T>[]
 */
export const getAllNodes = <T = any>(api: GridApi<T>): IRowNode<T>[] => {
  const nodes: IRowNode<T>[] = [];
  api.forEachNode(node => nodes.push(node));
  return nodes;
};

/**
 * Function helper to get all table nodes after filter.
 * @param api - GridApi
 * @return IRowNode[]
 */
export const getAllNodeAfterFilter = (api: GridApi): IRowNode[] => {
  const nodes: IRowNode[] = [];
  api.forEachNodeAfterFilter(node => nodes.push(node));
  return nodes;
};

export const editCellBelow = (api: GridApi, currentCell: CellPosition) => {
  const finalRowIndex = api.paginationGetRowCount() - 1;

  // If we are editing the last row in the grid, don't move to next line
  if (!currentCell || currentCell.rowIndex === finalRowIndex) {
    return;
  }

  fullEditStop(api);

  const nextRow = currentCell.rowIndex + 1;
  const currentColumn = currentCell.column.getColId();

  api.setFocusedCell(nextRow, currentColumn);
  api.startEditingCell({
    rowIndex: nextRow,
    colKey: currentColumn,
  });

  return true;
};

/* This code is used to format the comos values when they differ from our value. */

const IX_MARKER_START = '[*';
const IX_MARKER_END = '*]';
const IX_HTML_START = '<span class="bold">';
const IX_HTML_END = '</span>';
const COMOS_MARKER_START = '[#';
const COMOS_MARKER_END = '#]';
const COMOS_NUMERIC_PART_LENGTH = 3;
const COMOS_HTML_START = '<span class="color--n-60">';
const COMOS_HTML_END = '</span>';

const getIsFalsey = (value: any): value is null | undefined | number => {
  return getIsNullable(value) || !value;
};

export const addComosFormatting = (value: string | number, comosValue: string | number) => {
  if (getIsFalsey(comosValue)) {
    return value;
  }

  if (getIsFalsey(value)) {
    return `${COMOS_MARKER_START}(${comosValue})${COMOS_MARKER_END}`;
  }

  return `${IX_MARKER_START}${value}${IX_MARKER_END} ${COMOS_MARKER_START}(${comosValue})${COMOS_MARKER_END}`;
};

export const formatCell = (value: Maybe<string | number>) => {
  if (getIsFalsey(value)) {
    return '';
  }

  if (typeof value !== 'string') {
    return String(value);
  }

  return `${formatIxValue(value)} ${formatComosValue(value)}`.trim();
};

export const formatIxValue = (value: UNDEFINED<string>) => {
  if (getIsFalsey(value)) {
    return '';
  }

  if (getIsNotIxValue(value)) {
    return value.includes(COMOS_MARKER_START) ? value.substring(0, value.indexOf(COMOS_MARKER_START)) : value;
  }

  const formattedText = value.replace(IX_MARKER_START, IX_HTML_START).replace(IX_MARKER_END, IX_HTML_END);
  return formattedText.substring(0, formattedText.indexOf(IX_HTML_END) + IX_HTML_END.length);
};

export const formatComosValue = (value: UNDEFINED<string>) => {
  if (getIsComosValue(value)) {
    const formattedText = value
      .replace(COMOS_MARKER_START, COMOS_HTML_START)
      .replace(COMOS_MARKER_END, COMOS_HTML_END);
    return formattedText.substring(formattedText.indexOf(COMOS_HTML_START));
  }

  return '';
};

export const extractIxValue = (value: Maybe<string | number>) => {
  if (typeof value !== 'string') {
    return '';
  }

  if (getIsNotIxValue(value)) {
    return value.includes(COMOS_MARKER_START) ? value.substring(0, value.indexOf(COMOS_MARKER_START)) : value;
  }

  return value.substring(
    value.indexOf(IX_MARKER_START) + IX_MARKER_START.length,
    value.indexOf(IX_MARKER_END),
  );
};

export const extractComosValue = (value: Maybe<string | number>) => {
  if (typeof value !== 'string') {
    return value;
  }

  if (getIsNotComosValue(value)) {
    return '';
  }

  return value.substring(
    value.indexOf(COMOS_MARKER_START) + COMOS_NUMERIC_PART_LENGTH,
    value.indexOf(COMOS_MARKER_END) - 1,
  );
};

type ComosValue = string;
const getIsComosValue = (value: string | undefined): value is ComosValue => {
  return getIsNotNullable(value) && value.includes(COMOS_MARKER_START) && value.includes(COMOS_MARKER_END);
};
const getIsNotComosValue = (value: string | undefined) => {
  return !getIsComosValue(value);
};

type IxValue = `${typeof IX_MARKER_START}${string}${typeof IX_MARKER_END}`;
const getIsIxValue = (value: string | undefined): value is IxValue => {
  return getIsNotNullable(value) && value.includes(IX_MARKER_START) && value.includes(IX_MARKER_END);
};
const getIsNotIxValue = (value: string | undefined) => {
  return !getIsIxValue(value);
};

export function getIsColumn(col: Maybe<Column | ColumnGroup>): col is Column {
  return getIsNotNullable(col) && 'getId' in col;
}

/** Sentinel value used to represent blank/null/empty cell values in filter comparisons.
 * Must match the token used by WorksheetColumnFilterComponent. */
export const BLANK_FILTER_TOKEN = '__IX_BLANK__';

export const SKIP_HEADER_ON_RESIZE = false;

export function getIdsForResizableColumns(columnDefs: IxColDef[]): string[] {
  // if width is not set (in Property Defs.), then we want to return the field to auto-size the column
  return columnDefs.filter(colDef => !colDef.width).map(colDef => colDef.field);
}
